import type { Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppFormState, AppTextareaEvent } from '@shared/ui/fields'
import type { DisciplineStageComment, DisciplineStageCommentForm } from './interfaces'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import {
  useDisciplineStageCommentStoreApi,
  useDisciplineStageCommentUpdateApi,
} from '../api/stage-comment.hook'
import {
  buildStageCommentPayload,
  createStageCommentForm,
  isStageCommentReachMissing,
  isStageCommentSubmittable,
} from './discipline-comments'

interface DisciplineCommentFormOptions {
  /** The comment being edited; null opens the form for a new one. */
  comment: DisciplineStageComment | null
  onSaved: (comment: DisciplineStageComment) => void
  onCancel: () => void
}

interface DisciplineCommentFormReturn {
  refShowForm: Ref<boolean>
  refForm: Ref<DisciplineStageCommentForm>
  refChannel: WritableComputedRef<string[]>
  refReach: WritableComputedRef<string | undefined>
  refReachState: Ref<AppFormState>
  refIsDisabled: ComputedRef<boolean>
  refIsSaving: Ref<boolean>
  label: string
  isEditing: boolean
  channelOptions: ComputedRef<AppSelectOption[]>
  reachOptions: ComputedRef<AppSelectOption[]>
  showForm: () => void
  setComment: (event: AppTextareaEvent) => void
  cancel: () => void
  submit: () => Promise<void>
}

const REACH_MISSING = 'The "Reach" field was not filled.'

export const useDisciplineCommentForm = (options: DisciplineCommentFormOptions): DisciplineCommentFormReturn => {
  const route = useRoute()
  const toast = useToast()
  const listsStore = useListsStore()

  const isEditing = !!options.comment
  const label = isEditing ? 'Edit comment' : 'Add comment'
  const initial = isEditing ? createStageCommentForm(options.comment) : null

  const refShowForm = ref<boolean>(isEditing)
  const refForm = ref<DisciplineStageCommentForm>(createStageCommentForm(options.comment))
  const refReachState = ref<AppFormState>('static')
  const refIsSaving = ref<boolean>(false)

  const toOptions = (list: Record<string, string> | undefined): AppSelectOption[] =>
    Object.entries(list ?? {}).map(([value, text]) => ({ value, text }))

  const channelOptions = computed<AppSelectOption[]>(() => toOptions(listsStore.getLists?.stage_comments.channel.all))
  /** As in the legacy form, a reach can only be picked once a channel is. */
  const reachOptions = computed<AppSelectOption[]>(() =>
    refForm.value.channel.length ? toOptions(listsStore.getLists?.stage_comments.reach) : [])

  const refChannel = computed<string[]>({
    get: () => refForm.value.channel,
    set: (value) => {
      refForm.value.channel = value
      refReachState.value = 'static'
    },
  })

  const refReach = computed<string | undefined>({
    get: () => refForm.value.reach ?? undefined,
    set: (value) => {
      refForm.value.reach = value ?? null
      refReachState.value = 'static'
    },
  })

  const refIsDisabled = computed<boolean>(() => refIsSaving.value || !isStageCommentSubmittable(refForm.value, initial))

  const showForm = (): void => {
    refShowForm.value = true
  }

  const setComment = (event: AppTextareaEvent): void => {
    refForm.value.comment = event.value
  }

  const resetForm = (): void => {
    refForm.value = createStageCommentForm()
    refReachState.value = 'static'
    refShowForm.value = false
  }

  const cancel = (): void => {
    resetForm()
    options.onCancel()
  }

  const submit = async (): Promise<void> => {
    if (refIsDisabled.value) return

    if (isStageCommentReachMissing(refForm.value)) {
      refReachState.value = 'error'
      toast.warning(REACH_MISSING)
      return
    }

    const request = {
      disciplineId: route.params.id as string,
      payload: buildStageCommentPayload(refForm.value),
      onStart: () => {
        refIsSaving.value = true
      },
      onFinish: () => {
        refIsSaving.value = false
      },
    }

    const saved = options.comment
      ? await useDisciplineStageCommentUpdateApi({ ...request, commentId: options.comment.id })
      : await useDisciplineStageCommentStoreApi(request)

    if (!saved) return

    resetForm()
    options.onSaved(saved)
  }

  return {
    refShowForm,
    refForm,
    refChannel,
    refReach,
    refReachState,
    refIsDisabled,
    refIsSaving,
    label,
    isEditing,
    channelOptions,
    reachOptions,
    showForm,
    setComment,
    cancel,
    submit,
  }
}
